import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useWebSocket } from '#/contexts/WebsocketContext';
import { RecordingMode } from '#/types/preferences';
import logger from "#/utils/logger";

interface RecordingContextType {
  recordingEnabled: boolean;
  isRecording: boolean;
  recordingPaused: boolean;
  startRecording: () => Promise<void>;
  pauseRecording: () => Promise<void>;
  resumeRecording: () => Promise<void>;
  finishRecording: () => Promise<void>;
  cancelRecording: () => Promise<void>;
  recordingMode: RecordingMode;
  setRecordingMode: ( mode: RecordingMode ) => void;
  isMicAlwaysOn: boolean;
  setIsMicAlwaysOn: ( value: boolean ) => void;
}

const RecordingContext = createContext<RecordingContextType | undefined>(undefined);

export const RecordingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [recordingEnabled, setRecordingEnabled] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingPaused, setRecordingPaused] = useState(false);
  const [recordingMode, setRecordingMode] = useState<RecordingMode>(RecordingMode.Automatic);
  const [isMicAlwaysOn, setIsMicAlwaysOn] = useState<boolean>(false);
  const { startListening, stopListening, sendAudio } = useWebSocket();
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioNodeRef = useRef<AudioWorkletNode | null>(null);
  const isRecordingStarted = useRef<boolean>(false);   // True, when recording start has been triggered -> end of recording
  const recordingStartedRef = useRef<boolean>(false);  // True, when recording has been fully initialized -> end of recording
  const recordCancelRequested = useRef<boolean>(false);  // True, when recording has been cancelled before fully initialized
  const cancelledRef = useRef<boolean>(false);
  const isFirefoxRef = useRef<boolean>(false);

  const initializeAudioContext = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: { sampleRate: 16000 } });
    mediaStreamRef.current = stream;

    const audioContext = new AudioContext({ sampleRate: 16000 });
    audioContextRef.current = audioContext;

    await audioContext.audioWorklet.addModule('worklet/audio-processor.js');
    const audioNode = new AudioWorkletNode(audioContext, 'audio-processor');
    audioNodeRef.current = audioNode;

    const source = audioContext.createMediaStreamSource(stream);

    // Keep it in the graph but silent (no feedback):
    const sink = audioContext.createGain();
    sink.gain.value = 0;

    source.connect(audioNode);
    audioNode.connect(sink);
    sink.connect(audioContext.destination);

    if (audioContext.state === 'suspended') {
      await audioContext.resume();               // <-- ADD THIS
    }

    audioNode.port.postMessage('startRecording');
  };

  const initializeFirefoxAudioContext = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: { sampleRate: 48000 } });
    mediaStreamRef.current = stream;

    const audioContext = new AudioContext({ sampleRate: 48000 });
    audioContextRef.current = audioContext;
    await audioContext.audioWorklet.addModule('worklet/audio-downsampler.js')
    await audioContext.audioWorklet.addModule('worklet/audio-processor.js');
    const downsampler = new AudioWorkletNode(audioContext, 'audio-downsampler');
    const audioNode = new AudioWorkletNode(audioContext, 'audio-processor');
    audioNodeRef.current = audioNode;

    const source = audioContext.createMediaStreamSource(stream);
    source.connect(downsampler);
    downsampler.connect(audioNode);
    audioNode.connect(audioContext.destination);
    audioNode.port.postMessage('startRecording');
    audioNode.port.postMessage('downsample');
  }

  const cleanUpAudioContext = () => {
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    audioNodeRef.current = null;
  }

  useEffect(() => {
    const initAudio = async () => {
      try {
        // Check if the user allows recording
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // If successful, recording is enabled.
        setRecordingEnabled(true);
        logger.debug("The user has allowed recording")
        // Close recording for now
        stream.getTracks().forEach(track => track.stop());
      }
      catch (err) {
        logger.info("The user has declined the recording permission")
      }
    }

    initAudio();
    isFirefoxRef.current = CSS.supports("-moz-appearance: none");
    if (isFirefoxRef.current) {
      logger.info("Firefox browser detected");
    }

    return () => {
      cleanUpAudioContext();
    };
  }, []);

  const startRecording = async () => {
    if (recordingEnabled) {
      if (!isRecordingStarted.current) {
        logger.debug("Starting recording")
        isRecordingStarted.current = true;
        recordCancelRequested.current = false;

        if (!isMicAlwaysOn || recordingMode == RecordingMode.Automatic) {
          // Clean up every time if mic is not always on or in automatic mode
          cleanUpAudioContext();
        }

        if (!audioNodeRef.current || !mediaStreamRef.current) {
          if (isFirefoxRef.current) {
            await initializeFirefoxAudioContext();
          } else {
            await initializeAudioContext();
          }
        }
        else {
          audioNodeRef.current.port.postMessage('startRecording');
        }

        if (audioNodeRef.current && mediaStreamRef.current) {
          if (recordCancelRequested.current) {
            // While the recording has been set up, it has been cancelled
            logger.info("Recording cancelled before initialization complete");
            await cancelRecording();
          }
          else {
            startListening()

            // Audio processor callback
            const onMessage = (event: MessageEvent) => {
              if (event.data instanceof ArrayBuffer) {
                const intArray = new Uint8Array(event.data);
                sendAudio(intArray);
              }
              else if (event.data === 'bufferFlushed') {
                if (cancelledRef.current) {
                  stopListening(true);
                }
                else {
                  stopListening(false);
                }
              }
              else if (event.data === 'recordingStopped') {
                if (audioNodeRef.current) {
                  logger.debug("Removing event listener from processor")
                  audioNodeRef.current.port.onmessage = null;
                }
              }
              else if (typeof(event.data) === 'string' &&
                       event.data.startsWith('Compression')) {
                logger.debug(event.data)
              }
              else {
                logger.debug("RecordingContext unknown message " + event.data)
              }
            };

            audioNodeRef.current.port.onmessage = onMessage;
            mediaStreamRef.current.getTracks()[0].enabled = true;
            if (recordCancelRequested.current) {
              // While the recording has been set up, it has been cancelled
              logger.info("Recording cancelled before initialization complete");
              await cancelRecording();
            }
            else {
              setIsRecording(true);
              setRecordingPaused(false);
              recordingStartedRef.current = true;
            }
          }
        }
        else {
          logger.error("Could not initialize audio context")
        }
      }
    }
    else {
      logger.error("No media stream defined when starting recording")
    }
  };

  const finishRecording = async () => {
    if (mediaStreamRef.current && audioNodeRef.current && recordingStartedRef.current) {
      setIsRecording(false);
      setRecordingPaused(false);
      recordingStartedRef.current = false;
      cancelledRef.current = false;

      logger.debug("Finishing recording")
      mediaStreamRef.current.getTracks()[0].enabled = false;
      audioNodeRef.current.port.postMessage('stopRecording');
      cleanUpAudioContext();
      isRecordingStarted.current = false;
    }
    else if (isRecordingStarted.current) {
      // Recording has been started but not finalized - signal to end after initialization
      recordCancelRequested.current = true;
    }
    else {
      logger.debug("Trying to finish recording when media stream not initialized")
    }
  };

  const cancelRecording = async () => {
    if (mediaStreamRef.current && audioNodeRef.current && isRecordingStarted.current) {
      setIsRecording(false);
      setRecordingPaused(false);
      cancelledRef.current = true;

      logger.debug("Cancelling recording")
      mediaStreamRef.current.getTracks()[0].enabled = false;
      audioNodeRef.current.port.postMessage('stopRecording');
      cleanUpAudioContext();
      isRecordingStarted.current = false;
      recordingStartedRef.current = false;
    }
    else if (isRecordingStarted.current) {
      // Recording has been started but not finalized - signal to end after initialization
      recordCancelRequested.current = true;
    }
    else {
      logger.debug("Trying to cancel recording when media stream not initialized")
    }
  };

  const pauseRecording = async () => {
    if (isRecordingStarted.current && recordingMode == RecordingMode.Automatic && audioNodeRef.current) {
      logger.debug("Pausing recording")
      audioNodeRef.current.port.postMessage('pauseRecording');
      setRecordingPaused(true);
    }
  }

  const resumeRecording = async () => {
    // This function now handles both "resuming" and "starting fresh" in automatic mode.
    if (recordingMode === RecordingMode.Automatic) {
      if (recordingStartedRef.current && audioNodeRef.current) {
        // THE NORMAL CASE: A conversation is in progress and we are resuming a pause.
        logger.info("Resuming recording");
        audioNodeRef.current.port.postMessage('startRecording');
        setRecordingPaused(false);
      } else if (!isRecordingStarted.current) {
        // THE WELCOME MESSAGE CASE: Recording was never started. Let's start it now.
        logger.info("Recording not yet started. Kicking off automatic listening now.");
        await startRecording();
      }
    }
  };

  return (
    <RecordingContext.Provider value={{ recordingEnabled, isRecording, recordingPaused, startRecording,
                                        pauseRecording, resumeRecording, finishRecording, cancelRecording,
                                        recordingMode, setRecordingMode, isMicAlwaysOn, setIsMicAlwaysOn }}>
      {children}
    </RecordingContext.Provider>
  );
};

export const useRecording = () => {
  const context = useContext(RecordingContext);
  if (context === undefined) {
    throw new Error('useRecording must be used within a RecordingProvider');
  }
  return context;
};